/**
 * Servicio de Geolocalización
 * Sprint 2 - POC GPS Test
 * 
 * Wrapper sobre @capacitor/geolocation que provee:
 * - Solicitud de permisos
 * - Obtención de ubicación actual
 * - Watch de ubicación con callback
 * - Manejo normalizado de errores
 * 
 * NOTA: Este módulo es reutilizable para funcionalidades futuras
 */

import { Geolocation, type Position, type PermissionStatus } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import type {
    LocationData,
    LocationPermissionStatus,
    LocationWatchOptions,
    LocationError,
} from '../types';

// Re-export config for consumers
export { GPS_CONFIG } from '../types';

/**
 * Convierte Position de Capacitor a nuestra interfaz normalizada
 */
function normalizePosition(position: Position): LocationData {
    const { coords, timestamp } = position;

    return {
        latitude: coords.latitude,
        longitude: coords.longitude,
        accuracy: coords.accuracy,
        timestamp,
        altitude: coords.altitude,
        altitudeAccuracy: coords.altitudeAccuracy,
        heading: coords.heading,
        speed: coords.speed,
    };
}

/**
 * Convierte errores de Capacitor/Web a nuestro formato
 */
function normalizeError(error: unknown): LocationError {
    // Error de Capacitor
    if (error && typeof error === 'object' && 'message' in error) {
        const errObj = error as { message: string; code?: number };
        const message = errObj.message.toLowerCase();

        // Mapear códigos de error
        if (message.includes('permission') || message.includes('denied')) {
            return {
                code: 'PERMISSION_DENIED',
                message: 'Permiso de ubicación denegado',
            };
        }
        if (message.includes('unavailable') || message.includes('disabled')) {
            return {
                code: 'GPS_DISABLED',
                message: 'GPS no disponible o deshabilitado',
            };
        }
        if (message.includes('timeout')) {
            return {
                code: 'TIMEOUT',
                message: 'Tiempo de espera agotado al obtener ubicación',
            };
        }

        return {
            code: 'UNKNOWN',
            message: errObj.message || 'Error desconocido de ubicación',
        };
    }

    return {
        code: 'UNKNOWN',
        message: 'Error desconocido de ubicación',
    };
}

/**
 * Mapea el estado de permisos de Capacitor a nuestro enum
 */
function mapPermissionStatus(status: PermissionStatus): LocationPermissionStatus {
    const { location } = status;

    switch (location) {
        case 'granted':
            return 'granted';
        case 'denied':
            return 'denied';
        case 'prompt':
        case 'prompt-with-rationale':
            return 'prompt';
        default:
            return 'prompt';
    }
}

/**
 * Verifica si estamos en plataforma nativa (Android/iOS)
 */
export function isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
}

/**
 * Verifica el estado actual de permisos de ubicación
 */
export async function checkPermissions(): Promise<LocationPermissionStatus> {
    // En web, usar API de permisos del navegador
    if (!Capacitor.isNativePlatform()) {
        if (!navigator.geolocation) {
            return 'denied';
        }

        // Intentar obtener el estado de permisos (no todos los navegadores lo soportan)
        if ('permissions' in navigator) {
            try {
                const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
                return result.state === 'granted' ? 'granted' : result.state === 'denied' ? 'denied' : 'prompt';
            } catch {
                return 'prompt';
            }
        }
        return 'prompt';
    }

    // En nativo, usar Capacitor
    try {
        const status = await Geolocation.checkPermissions();
        return mapPermissionStatus(status);
    } catch (error) {
        console.warn('[LocationService] Error checking permissions:', error);
        return 'prompt';
    }
}

/**
 * Solicita permisos de ubicación al usuario
 * @returns Estado de permisos después de la solicitud
 */
export async function requestPermissions(): Promise<LocationPermissionStatus> {
    // En web, forzar solicitud de permiso llamando a getCurrentPosition
    if (!Capacitor.isNativePlatform()) {
        console.log('[LocationService] Requesting permissions (web)...');

        if (!navigator.geolocation) {
            return 'denied';
        }

        // Intentar obtener ubicación para forzar el prompt de permisos
        try {
            await new Promise<void>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    () => {
                        console.log('[LocationService] Permission granted (web)');
                        resolve();
                    },
                    (error) => {
                        console.error('[LocationService] Permission error (web):', error);
                        reject(error);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 0,
                    }
                );
            });
            return 'granted';
        } catch (error) {
            console.error('[LocationService] Permission denied (web):', error);
            return 'denied';
        }
    }

    // En nativo, usar Capacitor
    try {
        const status = await Geolocation.requestPermissions();
        return mapPermissionStatus(status);
    } catch (error) {
        console.error('[LocationService] Error requesting permissions:', error);
        throw normalizeError(error);
    }
}

/**
 * Obtiene la ubicación actual una sola vez
 */
export async function getCurrentPosition(
    options?: Partial<LocationWatchOptions>
): Promise<LocationData> {
    const mergedOptions = {
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: options?.timeout ?? 10000,
        maximumAge: options?.maximumAge ?? 0,
    };

    // En web, usar navigator.geolocation
    if (!Capacitor.isNativePlatform()) {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(normalizeError(new Error('Geolocation not supported')));
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        altitudeAccuracy: position.coords.altitudeAccuracy,
                        heading: position.coords.heading,
                        speed: position.coords.speed,
                        timestamp: position.timestamp,
                    });
                },
                (error) => {
                    reject(normalizeError(error));
                },
                mergedOptions
            );
        });
    }

    // En nativo, usar Capacitor
    try {
        const position = await Geolocation.getCurrentPosition(mergedOptions);
        return normalizePosition(position);
    } catch (error) {
        console.error('[LocationService] Error getting position:', error);
        throw normalizeError(error);
    }
}

/**
 * ID del watcher activo (solo puede haber uno a la vez)
 */
let activeWatchId: string | number | null = null;

/**
 * Inicia watch de ubicación con callbacks
 * @param onSuccess Callback cuando se obtiene nueva ubicación
 * @param onError Callback cuando ocurre un error
 * @param options Opciones de configuración
 * @returns Función para detener el watch
 */
export async function watchPosition(
    onSuccess: (location: LocationData) => void,
    onError: (error: LocationError) => void,
    options?: Partial<LocationWatchOptions>
): Promise<() => void> {
    // Si ya hay un watch activo, detenerlo primero
    if (activeWatchId) {
        await clearWatch();
    }

    const mergedOptions = {
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: options?.timeout ?? 10000,
        maximumAge: options?.maximumAge ?? 0,
    };

    // En web, usar navigator.geolocation.watchPosition
    if (!Capacitor.isNativePlatform()) {
        if (!navigator.geolocation) {
            throw normalizeError(new Error('Geolocation not supported'));
        }

        activeWatchId = navigator.geolocation.watchPosition(
            (position) => {
                onSuccess({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    altitude: position.coords.altitude,
                    altitudeAccuracy: position.coords.altitudeAccuracy,
                    heading: position.coords.heading,
                    speed: position.coords.speed,
                    timestamp: position.timestamp,
                });
            },
            (error) => {
                onError(normalizeError(error));
            },
            mergedOptions
        );

        console.log('[LocationService] Watch started (web) with ID:', activeWatchId);
        return clearWatch;
    }

    // En nativo, usar Capacitor
    try {
        activeWatchId = await Geolocation.watchPosition(
            mergedOptions,
            (position, err) => {
                if (err) {
                    onError(normalizeError(err));
                    return;
                }
                if (position) {
                    onSuccess(normalizePosition(position));
                }
            }
        );

        console.log('[LocationService] Watch started (native) with ID:', activeWatchId);

        // Retornar función para detener
        return clearWatch;
    } catch (error) {
        console.error('[LocationService] Error starting watch:', error);
        throw normalizeError(error);
    }
}

/**
 * Detiene el watch activo
 */
export async function clearWatch(): Promise<void> {
    if (activeWatchId !== null) {
        try {
            // En web, usar navigator.geolocation.clearWatch
            if (!Capacitor.isNativePlatform()) {
                if (typeof activeWatchId === 'number') {
                    navigator.geolocation.clearWatch(activeWatchId);
                    console.log('[LocationService] Watch cleared (web):', activeWatchId);
                }
            } else {
                // En nativo, usar Capacitor
                if (typeof activeWatchId === 'string') {
                    await Geolocation.clearWatch({ id: activeWatchId });
                    console.log('[LocationService] Watch cleared (native):', activeWatchId);
                }
            }
        } catch (error) {
            console.warn('[LocationService] Error clearing watch:', error);
        } finally {
            activeWatchId = null;
        }
    }
}

/**
 * Verifica si hay un watch activo
 */
export function isWatching(): boolean {
    return activeWatchId !== null;
}

/**
 * Abre la configuración del dispositivo para ajustes de ubicación
 * Solo funciona en plataformas nativas
 */
export async function openLocationSettings(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
        // En Android/iOS, podemos intentar abrir configuración
        // Esto requiere un plugin adicional o navegación manual
        console.log('[LocationService] Para habilitar ubicación, ve a Configuración > Ubicación');
    }
}

// Export como objeto para testing y consistencia
export const locationService = {
    isNativePlatform,
    checkPermissions,
    requestPermissions,
    getCurrentPosition,
    watchPosition,
    clearWatch,
    isWatching,
    openLocationSettings,
};
